import { z } from "zod";

/** The links an instructor can put on their card, in display order. */
export const INSTRUCTOR_LINK_KEYS = [
  "website",
  "youtube",
  "facebook",
  "linkedin",
  "x",
] as const;

/** The longest link we store. */
export const INSTRUCTOR_LINK_MAX = 200;

/**
 * The hosts each social link may point at, so a "YouTube" box cannot hold some
 * other site. A host matches itself and its subdomains (`www.`, `m.`).
 * The website link has no list: any https address is allowed.
 */
const SOCIAL_HOSTS: Record<string, readonly string[]> = {
  youtube: ["youtube.com", "youtu.be"],
  facebook: ["facebook.com", "fb.com"],
  linkedin: ["linkedin.com"],
  x: ["x.com", "twitter.com"],
};

export type InstructorLinkKey = (typeof INSTRUCTOR_LINK_KEYS)[number];

/**
 * Whether a link is an https address on the right site. Empty is valid: it
 * means "no link".
 *
 * @param key - Which link box it is for.
 * @param value - What was typed.
 * @returns True when the value is empty or a valid link for that box.
 */
export const isValidInstructorLink = (key: InstructorLinkKey, value: string) => {
  const trimmed = value.trim();

  if (trimmed === "") {
    return true;
  }

  if (trimmed.length > INSTRUCTOR_LINK_MAX) {
    return false;
  }

  let url: URL;

  try {
    url = new URL(trimmed);
  } catch {
    return false;
  }

  if (url.protocol !== "https:") {
    return false;
  }

  const hosts = SOCIAL_HOSTS[key];

  if (!hosts) {
    return true;
  }

  const host = url.hostname.toLowerCase();

  return hosts.some((allowed) => host === allowed || host.endsWith(`.${allowed}`));
};

const linkField = (key: InstructorLinkKey) =>
  z
    .string()
    .trim()
    .nullable()
    .optional()
    .refine((value) => value == null || isValidInstructorLink(key, value), {
      message: `Enter a full https:// address for ${key}`,
    });

/**
 * The links in `PATCH /v1/academy/instructor/me`. An empty or null link is
 * removed; the whole object replaces what was stored.
 */
export const instructorLinksInputSchema = z.object({
  website: linkField("website"),
  youtube: linkField("youtube"),
  facebook: linkField("facebook"),
  linkedin: linkField("linkedin"),
  x: linkField("x"),
});

/** The links as stored and shown: only the ones that are set. */
export const instructorLinksSchema = z.object({
  website: z.string().optional(),
  youtube: z.string().optional(),
  facebook: z.string().optional(),
  linkedin: z.string().optional(),
  x: z.string().optional(),
});

/**
 *
 */
export type InstructorLinksInput = z.infer<typeof instructorLinksInputSchema>;

/**
 *
 */
export type InstructorLinks = z.infer<typeof instructorLinksSchema>;

/**
 * Turns what was typed into what is stored: trimmed, empty ones dropped,
 * unknown keys ignored.
 *
 * @param input - The links from the form.
 * @returns Only the links that are set.
 */
export const cleanInstructorLinks = (input: InstructorLinksInput): InstructorLinks => {
  const out: InstructorLinks = {};

  for (const key of INSTRUCTOR_LINK_KEYS) {
    const value = input[key]?.trim();

    if (value) {
      out[key] = value;
    }
  }

  return out;
};
